// src/Home.jsx
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-fade';
import HomeBg from './assets/things/HomeBg.jpg';
import useCars from './data/useCars';
import { TIERS, BASIS, rateLabel } from './data/pricing';
import './index.css';

const TURO_PROFILE = 'https://turo.com/us/en/drivers/33571187';

const OWNER_POINTS = [
  'Listings, pricing and every guest message',
  'Cleaning, delivery and 100+ photos every trip',
  'Damage claims and maintenance runs handled',
];

const reduceMotion =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function HomePage() {
  const cars = useCars();

  // Exotics first in the hero carousel, everything else in fleet order
  const heroCars = useMemo(
    () =>
      [...cars]
        .sort((a, b) => Number(b.tier === 'exotic') - Number(a.tier === 'exotic'))
        .slice(0, 10),
    [cars]
  );

  const stats = [
    { value: cars.length, label: 'Vehicles in the fleet' },
    { value: cars.filter((c) => c.tier === 'exotic').length, label: 'Exotic and performance' },
    { value: cars.filter((c) => c.tag === 'EV' || c.tag === 'Hybrid').length, label: 'EV and hybrid' },
    { value: '5-star', label: 'All-Star Host on Turo' },
  ];

  return (
    <>
      {/* ---------- Hero ---------- */}
      <section className="relative isolate flex" style={{ minHeight: 'min(calc(100svh - 4rem), 50rem)' }}>
        <img src={HomeBg} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" />
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'linear-gradient(100deg, rgba(14,13,11,0.96) 0%, rgba(14,13,11,0.84) 50%, rgba(14,13,11,0.5) 100%)',
          }}
        />

        <div className="container-x grid items-center self-center gap-10 py-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-14 lg:py-20">
          <div className="min-w-0">
            <h1 className="display text-4xl min-[400px]:text-5xl sm:text-6xl">Drive something unforgettable.</h1>
            <p className="mt-6 max-w-md text-lg" style={{ color: 'rgba(244,239,230,0.82)' }}>
              Stylish, clean vehicles ready for your next Vegas trip: airport pickups, Strip cruising or a
              weekend getaway.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/available-cars" className="btn btn-primary">
                Browse the fleet
              </Link>
              <Link to="/fleet-management" className="btn btn-ghost">
                Own a car? Get it managed
              </Link>
            </div>

            <p className="muted mt-8 text-sm">
              5-star All-Star Host on Turo.{' '}
              <a className="link-quiet" href={TURO_PROFILE} target="_blank" rel="noopener noreferrer">
                See our Turo profile
              </a>
            </p>
          </div>

          <div className="mx-auto w-full min-w-0 max-w-xl lg:max-w-none">
            <Swiper
              modules={[Autoplay, EffectFade]}
              effect="fade"
              fadeEffect={{ crossFade: true }}
              autoplay={reduceMotion ? false : { delay: 3500, disableOnInteraction: false }}
              loop
              className="w-full overflow-hidden rounded-2xl"
              style={{ border: '1px solid var(--line)', maxWidth: '100%' }}
            >
              {heroCars.map((car) => (
                <SwiperSlide key={car.id}>
                  <a
                    href={car.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative block aspect-[4/3]"
                  >
                    <img src={car.image} alt={car.name} className="h-full w-full object-cover object-center" />
                    <div
                      className="absolute inset-x-0 bottom-0 p-5 pt-16"
                      style={{ background: 'linear-gradient(to top, rgba(14,13,11,0.92), transparent)' }}
                    >
                      <p className="display text-xl">{car.name}</p>
                      <p className="text-sm" style={{ color: 'var(--bronze-text)' }}>
                        {car.tag}
                      </p>
                    </div>
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      {/* ---------- Fleet numbers ---------- */}
      <section style={{ borderBlock: '1px solid var(--line)', background: 'var(--surface)' }}>
        <dl className="container-x grid grid-cols-2 gap-y-6 py-8 md:grid-cols-4">
          {stats.map(({ value, label }) => (
            <div key={label} className="flex flex-col-reverse pr-4">
              <dt className="muted text-sm">{label}</dt>
              <dd className="display text-4xl" style={{ color: 'var(--bronze-text)' }}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ---------- Owner offer ---------- */}
      <section
        className="paper section-y relative isolate overflow-hidden"
        style={{
          background:
            'radial-gradient(60rem 30rem at 100% 0%, rgba(194,128,58,0.16), transparent 60%), radial-gradient(40rem 24rem at 0% 100%, rgba(194,128,58,0.10), transparent 60%), var(--paper)',
        }}
      >
        <div className="container-x grid items-center gap-12 lg:grid-cols-[1fr_1fr] lg:gap-16">
          <div>
            <p className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: '#9a5f22' }}>
              <span className="inline-block h-px w-8" style={{ background: 'var(--bronze)' }} aria-hidden="true" />
              For car owners
            </p>
            <h2 className="display mt-4 text-4xl sm:text-5xl" style={{ textWrap: 'balance' }}>
              Own a car in Las Vegas? We run it on <span style={{ color: '#9a5f22' }}>Turo for you.</span>
            </h2>
            <p className="muted mt-5 max-w-md text-lg">
              Your car earns while we do the work. You pay a percentage of what it makes, set by vehicle class.
            </p>

            <ul className="mt-7 grid gap-3">
              {OWNER_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                    style={{ background: 'var(--paper-ink)', color: 'var(--bronze-text)' }}
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="font-medium">{point}</span>
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Link to="/fleet-management" className="btn btn-primary">
                See pricing and apply
              </Link>
              <Link to="/fleet-management#rates" className="font-semibold underline underline-offset-4" style={{ textDecorationColor: 'var(--bronze)' }}>
                Estimate your earnings
              </Link>
            </div>
          </div>

          {/* Fee card */}
          <div
            className="on-dark relative rounded-3xl p-7 sm:p-9"
            style={{
              background: 'linear-gradient(160deg, #1d1a16 0%, #0e0d0b 100%)',
              color: 'var(--ink)',
              border: '1px solid rgba(194,128,58,0.35)',
              boxShadow: '0 30px 60px -20px rgba(22,19,15,0.45), 0 12px 24px -12px rgba(22,19,15,0.3)',
            }}
          >
            <div
              className="absolute inset-x-8 top-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, var(--bronze-text), transparent)' }}
              aria-hidden="true"
            />
            <p className="text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--bronze-text)' }}>
              Our management fee
            </p>
            <p className="muted mt-2 text-sm">Taken from {BASIS}.</p>

            <ul className="mt-6">
              {TIERS.map((tier) => (
                <li
                  key={tier.key}
                  className="flex items-center justify-between gap-6 py-5"
                  style={{ borderTop: '1px solid var(--line)' }}
                >
                  <span className="text-lg font-semibold">{tier.label}</span>
                  <span className="display shrink-0 text-4xl sm:text-5xl" style={{ color: 'var(--bronze-text)' }}>
                    {rateLabel(tier)}
                  </span>
                </li>
              ))}
            </ul>

            <p className="muted pt-5 text-sm" style={{ borderTop: '1px solid var(--line)' }}>
              Run by a 5-star All-Star Host on Turo.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
